import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {Button} from 'primeng/button';
import {FormsModule} from '@angular/forms';
import {NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase} from '@angular/common';
import {delay, finalize, Subject, Subscription, take} from 'rxjs';
import {WebSocketService} from '../../../shared/services/webscoket.service';
import {debounceTime, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';
import {v4 as uuidv4} from 'uuid';
import {AiInsights, Message, MessageMimeType, WsMessage, WSMessageType} from '../../contracts/message.interface';
import {Tooltip} from 'primeng/tooltip';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {ConversationService} from '../../services/conversation.service';
import {Conversation, ConversationPriority, ConversationStatus} from '../../contracts/conversation.interface';
import {Textarea} from 'primeng/textarea';
import {TemplateService} from '../../../templates/services/template.service';
import {Dialog} from 'primeng/dialog';
import {Template} from '../../../templates/contracts/template.interface';
import {InputText} from 'primeng/inputtext';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {MessageBubbleComponent} from '../message/message-bubble/message-bubble.component';
import {ContactDisplayPipe} from '../../../shared/pipes/contact-display.pipe';
import {NotificationService} from '../../../shared/services/notification.service';
import {Divider} from 'primeng/divider';
import {ChatSidePanelMediaComponent} from '../chat-sidepanel/chat-sidepanel-media/chat-sidepanel-media.component';
import {CONVERSATION_AVATAR_MAP} from '../../../shared/constants/conversation-avatar-map';
import {
  ConversationInitialsComponent
} from '../../../shared/components/conversation-initials/conversation-initials.component';
import {Message as PrimeMessage} from 'primeng/message';
import {ChatReplyContextComponent} from '../chat-reply-context/chat-reply-context.component';
import {ChatHistoryDrawerComponent} from '../chat-context-drawer/chat-history-drawer.component';
import {TenantSettings} from '../../../tenants/contracts/tenant_settings.interface';

@Component({
  selector: 'kc-chat',
  imports: [
    Button,
    FormsModule,
    NgForOf,
    NgClass,
    Tooltip,
    LoaderComponent,
    Textarea,
    Dialog,
    InputText,
    IconField,
    InputIcon,
    MessageBubbleComponent,
    ContactDisplayPipe,
    NgSwitchCase,
    NgSwitch,
    ChatSidePanelMediaComponent,
    NgIf,
    ConversationInitialsComponent,
    PrimeMessage,
    ChatReplyContextComponent,
    ChatHistoryDrawerComponent,
  ],
  providers: [NotificationService],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit, OnDestroy, OnChanges {
  @Input() conversation!: Conversation;
  @Output() closeConversationEvent = new EventEmitter<any>();
  @Output() unselectConversation = new EventEmitter<any>();
  @Output() unselectConversationAndReopen = new EventEmitter<any>();
  @Input() isReadOnly = false;
  @Input() closedByMe = false;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  notesOpen: boolean = false;
  notesText: string = '';
  notesSaveStatus: 'idle' | 'saving' | 'saved' = 'idle';
  private notesSubject = new Subject<string>();
  private notesSubscription?: Subscription;
  conversationAvatarMap = CONVERSATION_AVATAR_MAP;

  loading: boolean = false;
  chatMessages: any = [];
  newMessage: string = '';
  selectedFile: File | null = null;
  isMediaPanelOpen: boolean = false;
  templateDialogVisible: boolean = false;
  isQuote: boolean = false;
  private subscription?: Subscription;
  private aiInsightsSubscription?: Subscription;
  private pendingAiInsights = new Map<number, Partial<AiInsights>>();
  activePanel: 'media' | null = null;
  tenantSettings?: TenantSettings;

  page: number = 0;
  limit: number = 10;
  hasMoreMessages: boolean = true;
  isDragOver = false;
  dragSide: 'left' | 'right' | null = null;

  /* For dialog */
  searchText: string = '';
  tagFilter: string = '';
  filteredTemplates: Template[] = [];
  selectedTemplate?: Template;

  templates: Template[] = [];

  previewFileUrl?: string | null;
  previewFileName?: string | null;

  selectedReplyMessage? : Message;

  // Drawer contesto reply
  drawerVisible: boolean = false;
  drawerConversationId!: number;
  drawerCenterMessageId?: number;

  priorities = [
    { label: 'Nessuna', value: ConversationPriority.DEFAULT},
    { label: 'Bassa', value: ConversationPriority.LOW},
    { label: 'Media', value: ConversationPriority.MEDIUM},
    { label: 'Alta', value: ConversationPriority.HIGH},
  ];

  constructor(
    private webSocketService: WebSocketService,
    private conversationService: ConversationService,
    private templateService: TemplateService,
    private notificationService: NotificationService,
    private ngZone: NgZone,
    private cd: ChangeDetectorRef
  ) {
  }

  /** Angular Lifecycle Hooks */

  ngOnInit() {
    this.tenantSettings = JSON.parse(localStorage.getItem('tenantSettings') || '{}');
    this.subscription = this.webSocketService.messages$
      .pipe(filter(msg => msg.type === WSMessageType.CHAT))
      .subscribe(msg => {
        this.onReadMessage(msg);
      });

    this.aiInsightsSubscription = this.webSocketService.messages$
      .pipe(filter(msg => msg.type === WSMessageType.AI_INSIGHTS_UPDATED))
      .subscribe(msg => {
        this.updateMessageAiInsights(msg.content.messageId, msg.content.aiInsights);
      });

    this.notesSubscription = this.notesSubject.pipe(
      debounceTime(800),
      distinctUntilChanged(),
      switchMap(notes => {
        this.notesSaveStatus = 'saving';
        return this.conversationService.updateNotes(this.conversation.id, notes || null);
      }),
    ).subscribe(() => {
      this.conversation.notes = this.notesText;
      this.notesSaveStatus = 'saved';
    });

    this.chatMessages = [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['conversation'] && changes['conversation'].currentValue) {
      const prevConv: Conversation | undefined = changes['conversation'].previousValue;
      const newConv: Conversation = changes['conversation'].currentValue;

      this.selectedFile = null;
      this.isQuote = false;

      if (!prevConv || prevConv.id !== newConv.id) {
        this.notesText = newConv.notes ?? '';
        this.resetConversationState();
        this.onLoadMessages();
      }
    }
  }

  openNotesPopover(event: Event) {
    this.notesText = this.conversation.notes ?? '';
    this.notesSaveStatus = 'idle';
    this.notesOpen = true;
  }

  closeNotes() {
    this.notesOpen = false;
  }

  onNotesInput(value: string) {
    this.notesSaveStatus = 'idle';
    this.notesSubject.next(value.trim());
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.aiInsightsSubscription?.unsubscribe();
    this.notesSubscription?.unsubscribe();
  }


  /** Conversation Functions */

  onCloseConversation() {
    this.conversationService.closeConversation(this.conversation.id).pipe(take(1)).subscribe(_ => this.closeConversationEvent.emit(this.conversation.id));
  }

  onRevokeConversation(){
      this.conversationService.revokeAssignment(this.conversation.id).subscribe(data => {
        this.unselectConversation.emit();
      });
  }

  onReopenConversation(){
    this.conversationService.reopenConversation(this.conversation.id).subscribe(data => {
      this.unselectConversationAndReopen.emit();
    });
  }

  openContext(message: Message) {
    this.drawerConversationId = message.conversationId;
    this.drawerCenterMessageId = message.replyMessageId!;
    this.drawerVisible = true;
  }

  sendTemplateMessage(){
    this.conversationService.sendMetaTemplate(this.conversation.id).subscribe(data => {
      console.log(data)
      this.notificationService.success('Messaggio di template inviato con successo.');
    });
  }

  private resetConversationState(): void {
    this.chatMessages = [];
    this.pendingAiInsights.clear();
    this.page = 0;
    this.limit = 10;
    this.hasMoreMessages = true;
    this.loading = false;

    this.newMessage = '';
    this.selectedFile = null;
    this.isQuote = false;

    this.previewFileUrl = null;
    this.previewFileName = null;

    this.activePanel = null;
    this.templateDialogVisible = false;
  }

  /** Messages Functions */

  private onLoadMessages() {
    this.loading = true;
    this.conversationService.getMessages(this.conversation.id, this.page, this.limit).pipe(
      take(1)
    ).subscribe(data => {
      this.chatMessages = [...data].reverse().map(m => this.applyPendingInsights(this.normalizeMessage(m)));

      this.cd.detectChanges();

      this.scrollToBottom('auto', () => {
        this.loading = false;
        this.checkAndFillContainer();
      });
    });
  }

  private onLoadMoreMessages() {
    if (this.loading || !this.hasMoreMessages) return;

    this.loading = true;
    const previousScrollHeight = this.messagesContainer.nativeElement.scrollHeight;

    this.page++;

    this.conversationService.getMessages(this.conversation.id, this.page, this.limit).pipe(
      delay(300),
      take(1),
    ).subscribe({
      next: data => {
        if (!data.length) {
          this.hasMoreMessages = false;
          this.page--;
          this.loading = false;
          return;
        }

        this.chatMessages = [...data.reverse(), ...this.chatMessages];
        this.cd.detectChanges();

        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            const container = this.messagesContainer.nativeElement;
            container.scrollTop = container.scrollHeight - previousScrollHeight;
            // loading=false AFTER scrollTop is set so the resulting scroll event
            // does not re-trigger onLoadMoreMessages while we are still "busy"
            this.ngZone.run(() => {
              this.loading = false;
              this.checkAndFillContainer();
            });
          }, 50);
        });
      },
      error: () => {
        this.loading = false;
        this.page--;
      },
    });
  }

  getPriorityLabel(priority: ConversationPriority) {
    return this.priorities.find(p => p.value === priority)?.label;
  }

  getPriorityIcon(priority: ConversationPriority): string {
    const map: Record<ConversationPriority, string> = {
      [ConversationPriority.HIGH]:    'fa-duotone fa-solid fa-signal-bars',
      [ConversationPriority.MEDIUM]:  'fa-duotone fa-solid fa-signal-bars-fair',
      [ConversationPriority.LOW]:     'fa-duotone fa-solid fa-signal-bars-weak',
      [ConversationPriority.DEFAULT]: 'fa-duotone fa-solid fa-signal-bars-slash',
    };
    return map[priority] ?? 'fa-duotone fa-solid fa-signal-bars-slash';
  }


  private onReadMessage(msg: WsMessage) {
    if (msg.isErrorMessage) {
      this.handleErrorMessage(msg);
    } else {
      this.handleMessages(msg);
    }
  }

  private handleMessages(msg: WsMessage) {
    const normalized = this.normalizeMessage(msg.content);
    const index = this.chatMessages.findIndex((message: Message) => message.messageUuid === normalized.messageUuid);

    if (index > -1) {
      this.chatMessages = [
        ...this.chatMessages.slice(0, index),
        this.applyPendingInsights(normalized),
        ...this.chatMessages.slice(index + 1)
      ];
    } else {
      if (this.conversation.id === normalized.conversationId) {
        this.chatMessages = [...this.chatMessages, this.applyPendingInsights(normalized)];
        this.cd.detectChanges();
        this.scrollToBottom('smooth');
      }
    }
  }

  private updateMessageAiInsights(messageId: number, patch: Partial<AiInsights>): void {
    const index = this.chatMessages.findIndex((m: Message) => Number(m.id) === Number(messageId));
    if (index === -1) {
      const existing = this.pendingAiInsights.get(messageId) ?? {};
      this.pendingAiInsights.set(messageId, { ...existing, ...patch });
      return;
    }
    const message = this.chatMessages[index];
    this.chatMessages = [
      ...this.chatMessages.slice(0, index),
      { ...message, aiInsights: { ...(message.aiInsights ?? {}), ...patch } },
      ...this.chatMessages.slice(index + 1),
    ];
    this.cd.detectChanges();
  }

  private applyPendingInsights(message: Message): Message {
    if (!message.id) return message;
    const pending = this.pendingAiInsights.get(message.id);
    if (!pending) return message;
    this.pendingAiInsights.delete(message.id);
    return { ...message, aiInsights: { ...(message.aiInsights ?? {}), ...pending } };
  }

  private normalizeMessage(msg: any): any {
    if (!msg?.aiInsights) return msg;
    const raw = msg.aiInsights;
    const insights: AiInsights = {};
    if (raw.audioTranscription) insights.audioTranscription = raw.audioTranscription;
    if (raw.plateDetection) insights.plateDetection = raw.plateDetection;
    return { ...msg, aiInsights: Object.keys(insights).length ? insights : null };
  }

  private handleErrorMessage(msg: WsMessage) {
    console.log('test');
    if (msg.errorContent.type === 'SESSION_EXPIRED') {
      const index = this.chatMessages.findIndex((message: Message) => message.messageUuid === msg.content.messageUuid);

      if (index > -1) {
       this.chatMessages[index].failedToSend = true;
      }
    }

    // TODO for now we ignore all errors, we don't want to show to the user anything related to the error.
  }


  /** Input Functions */

  onPaste(event: ClipboardEvent) {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          this.onResetFileInput();
          this.selectedFile = file;
          this.onCreateFilePreview(file);
          event.preventDefault();
        }
        break; // single file only
      }
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.dragSide = event.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.dragSide = null;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.isQuote = event.clientX >= rect.left + rect.width / 2;
    this.isDragOver = false;
    this.dragSide = null;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.onResetFileInput();
      this.selectedFile = file;
      this.onCreateFilePreview(file);
    }
  }

  onCreateFilePreview(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      // e.target.result contiene il base64
      // puoi usare questo per la preview
      this.previewFileUrl = e.target.result;
      this.previewFileName = file.name;
    };
    // solo per immagini usa readAsDataURL, altrimenti puoi mostrare icona + nome
    if (file.type.startsWith('image/')) {
      reader.readAsDataURL(file);
    } else {
      // altri file: puoi usare solo nome e icona
      this.previewFileUrl = null;
      this.previewFileName = file.name;
    }
  }

  onSendMessage() {
    if (this.selectedFile) {
      this.onUploadFile();
      return;
    }

    if (!this.newMessage) {
      return;
    }

    const chatMessage = {
      messageUuid: uuidv4(),
      text: this.newMessage,
      type: 'S',
      conversationId: this.conversation.id,
      mimeType: MessageMimeType.TEXT,
      status: 'SENT'
    } as Message;

    if(this.selectedReplyMessage){
      console.log(this.selectedReplyMessage.id)
      chatMessage.replyMessageId = this.selectedReplyMessage.id;
    }

    this.webSocketService.sendMessage(
      {
        type: 'chat',
        content: chatMessage
      } as WsMessage);

    if(this.selectedReplyMessage){
      chatMessage.replyMessageId = this.selectedReplyMessage.id
      chatMessage.replyContext = {"from_": this.conversation.fromNumber, 'id': this.selectedReplyMessage?.messageId}
      chatMessage.replyMessage = this.selectedReplyMessage
    }

    this.chatMessages.push(chatMessage);
    // this.chatMessages = [...this.chatMessages, chatMessage];


    // TODO we should show the message and it's in "sending" status, after we get a confirm from message sent,
    // TODO we can confirm to the user or else give an error

    this.newMessage = '';
    this.selectedReplyMessage = undefined;
  }

  onTriggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  onToggleQuote() {
    this.isQuote = !this.isQuote;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.isQuote = false;
      this.onCreateFilePreview(input.files[0]);
    }
  }

  onUploadFile() {
    if (!this.selectedFile) return;

    this.loading = true;

    this.conversationService
      .uploadFile(
        this.conversation.id,
        this.selectedFile,
        this.isQuote,
        this.selectedReplyMessage?.id ?? null
      )
      .pipe(
        finalize(() => {
          this.selectedReplyMessage = undefined;
          this.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.notificationService.success('File inviato con successo.');
          this.onResetFileInput();
          this.isQuote = false;
          this.scrollToBottom();
        },
        error: (error: any) => {
          if (error.error.code === "SESSION_EXPIRED") {
            this.notificationService.error('Sessione scaduta. Per favore invia un messaggio di template');
            return;
          } else {
            console.error('Errore upload:', error);
            this.notificationService.error('Errore durante il caricamento del file.');
          }
        }
      });
  }

  onResetFileInput() {
    if (this.previewFileUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewFileUrl);
    }
    this.selectedFile = null;
    this.previewFileUrl = null;
    this.previewFileName = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // stop newline
      this.onSendMessage();
      this.scrollToBottom();
    }
  }


  /** Side Panel Functions */

  onToggleMediaPanel() {
    this.activePanel = this.activePanel === 'media' ? null : 'media';
  }


  /** Templates Functions */

  onSelectTemplate(template: Template): void {
    this.newMessage = template.text;
    this.templateDialogVisible = false;
  }

  onOpenTemplates() {
    this.templateService.getAll(1, 1000).pipe(take(1)).subscribe(data => {
      this.templates = data.data;
      this.filteredTemplates = [...this.templates];
      this.templateDialogVisible = true;
    })
  }

  onFilterTemplates() {
    this.filteredTemplates = this.templates.filter(t =>
      t.text.toLowerCase().includes(this.searchText.toLowerCase()) &&
      ('info generico testo').toLowerCase().includes(this.tagFilter.toLowerCase())
    );
  }


  /** UI Behavior Functions */

  private scrollToBottom(behavior: ScrollBehavior = 'smooth', onDone?: () => void): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const container = this.messagesContainer?.nativeElement;
        if (!container) { this.ngZone.run(() => onDone?.()); return; }

        const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
        const pending = images.filter(img => !img.complete);

        const doScroll = () => {
          container.scrollTo({ top: container.scrollHeight, behavior });
          this.ngZone.run(() => onDone?.());
        };

        if (!pending.length) { doScroll(); return; }

        let done = 0;
        const onLoad = () => { if (++done === pending.length) doScroll(); };
        pending.forEach(img => {
          img.addEventListener('load', onLoad, { once: true });
          img.addEventListener('error', onLoad, { once: true });
        });
      }, 50);
    });
  }

  onScroll() {
    const container = this.messagesContainer.nativeElement;
    if (container.scrollTop < 50) {
      this.onLoadMoreMessages();
    }
  }

  private checkAndFillContainer(): void {
    const container = this.messagesContainer?.nativeElement;
    // clientHeight === 0 means the container is not yet laid out (e.g. inside a
    // dialog whose animation hasn't completed). Skip to avoid a scroll-to-top loop.
    if (!container || !this.hasMoreMessages || container.clientHeight === 0) return;
    if (container.scrollHeight <= container.clientHeight) {
      this.onLoadMoreMessages();
    }
  }

  handleReplySelection(message: Message){
    this.selectedReplyMessage = message;
  }

  onClearReply(){
    this.selectedReplyMessage = undefined;
  }

  protected readonly ConversationStatus = ConversationStatus;
}
